import React, {
    lazy,
    Suspense,
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    useCookieConsent,
} from "../../cookie-consent/provider";

const LazyContactMap = lazy(
    () => import("./ContactMap")
);

function MapPlaceholder({
    title,
    description,
    actionLabel,
    onAction,
    isError = false,
}) {
    return (
        <div
            className={`contacts-page__map-placeholder${
                isError
                    ? " contacts-page__map-placeholder--error"
                    : ""
            }`}
            role={isError ? "alert" : undefined}
        >
            <span
                className="contacts-page__map-placeholder-icon"
                aria-hidden="true"
            />

            <h3>{title}</h3>
            <p>{description}</p>

            {actionLabel && onAction && (
                <button
                    type="button"
                    onClick={onAction}
                >
                    {actionLabel}
                </button>
            )}
        </div>
    );
}

export function ContactsMapSection({
    t,
    MapComponent = LazyContactMap,
}) {
    const {
        isFunctionalEnabled,
        openSettings,
    } = useCookieConsent();

    const [mapStatus, setMapStatus] =
        useState("loading");

    useEffect(() => {
        if (!isFunctionalEnabled) {
            setMapStatus("loading");
        }
    }, [isFunctionalEnabled]);

    const handleReady = useCallback(() => {
        setMapStatus("ready");
    }, []);

    const handleError = useCallback(() => {
        setMapStatus("failed");
    }, []);

    let mapContent;

    if (!isFunctionalEnabled) {
        mapContent = (
            <MapPlaceholder
                title={t(
                    "main.contactsPage.map.consentTitle"
                )}
                description={t(
                    "main.contactsPage.map.consentDescription"
                )}
                actionLabel={t(
                    "main.contactsPage.map.consentAction"
                )}
                onAction={openSettings}
            />
        );
    } else if (mapStatus === "failed") {
        mapContent = (
            <MapPlaceholder
                title={t(
                    "main.contactsPage.map.errorTitle"
                )}
                description={t(
                    "main.contactsPage.map.errorDescription"
                )}
                isError
            />
        );
    } else {
        mapContent = (
            <Suspense
                fallback={
                    <MapPlaceholder
                        title={t(
                            "main.contactsPage.map.loading"
                        )}
                        description={t(
                            "main.contactsPage.map.loadingDescription"
                        )}
                    />
                }
            >
                <div className="contacts-page__map-canvas">
                    <MapComponent
                        markerTitle={t(
                            "main.contactsPage.map.markerTitle"
                        )}
                        address={t(
                            "main.contactsPage.map.markerAddress"
                        )}
                        onReady={handleReady}
                        onError={handleError}
                    />

                    {mapStatus === "loading" && (
                        <div
                            className="contacts-page__map-loading"
                            role="status"
                            aria-live="polite"
                        >
                            <span aria-hidden="true"/>
                            {t(
                                "main.contactsPage.map.loading"
                            )}
                        </div>
                    )}
                </div>
            </Suspense>
        );
    }

    return (
        <section
            className="contacts-page__map-section"
            aria-labelledby="contacts-map-title"
        >
            <div className="contacts-page__section-heading">
                <span>
                    {t(
                        "main.contactsPage.map.eyebrow"
                    )}
                </span>

                <h2 id="contacts-map-title">
                    {t(
                        "main.contactsPage.map.title"
                    )}
                </h2>

                <p>
                    {t(
                        "main.contactsPage.map.intro"
                    )}
                </p>
            </div>

            <div
                className="contacts-page__map-panel"
                role="region"
                aria-label={t(
                    "main.contactsPage.map.ariaLabel"
                )}
                aria-busy={
                    isFunctionalEnabled &&
                    mapStatus === "loading"
                }
            >
                {mapContent}
            </div>
        </section>
    );
}
